import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {NgxEchartsDirective} from 'ngx-echarts';
import type {EChartsOption} from 'echarts';
import {TradeApiService} from '../trade/trade-api.service';
import {
  BusinessStockDto,
  ChatContactView,
  SaleOfferDto,
  TradeRecordDto
} from '../../../models/generated-models';
import {formatQuantityWithUnit} from '../trade/trade-utils';
import {ChatRestService} from '../chat/chat-rest.service';
import {FruitIconComponent} from '../../shared/commons/fruit-icon/fruit-icon.component';
import {formatMessageTime} from '../../shared/commons/date-utils';
import {PageTitleComponent} from '../../shared/commons/page-title/page-title.component';
import {ThaiDateTimePipe} from '../../shared/commons/thai-datetime.pipe';

interface YearOption {
  gregorian: number;
  buddhist: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RouterLink, FruitIconComponent, NgxEchartsDirective, PageTitleComponent, ThaiDateTimePipe],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit {
  private readonly tradeApi = inject(TradeApiService);
  private readonly chatApi = inject(ChatRestService);
  private readonly currentGregorianYear = new Date().getFullYear();
  protected readonly formatMessageTime = formatMessageTime;
  protected readonly loading = signal(true);
  protected readonly loadingProductNames = signal(true);
  protected readonly records = signal<TradeRecordDto[]>([]);
  protected readonly unsoldRecords = signal<TradeRecordDto[]>([]);
  protected readonly offers = signal<SaleOfferDto[]>([]);
  protected readonly stock = signal<BusinessStockDto[]>([]);
  protected readonly productNames = signal<string[]>([]);
  protected readonly yearOptions: YearOption[] = Array.from({length: 11}, (_, offset) => {
    const gregorian = this.currentGregorianYear - offset;
    return {gregorian, buddhist: gregorian + 543};
  });
  protected readonly selectedYear = signal(this.currentGregorianYear);
  protected readonly selectedProductName = signal('');
  protected readonly contacts = signal<ChatContactView[]>([]);
  protected readonly filteredRecords = computed(() =>
    this.records().filter((record) => this.matchesDashboardFilters(record)));
  protected readonly filteredUnsoldRecords = computed(() =>
    this.unsoldRecords().filter((record) => this.matchesDashboardFilters(record)));
  private readonly soldRecords = computed(() =>
    this.filteredRecords().filter((r) => this.soldQty(r) > 0));
  protected readonly latestSold = computed(() => this.soldRecords().slice(0, 5));
  protected readonly totalCount = computed(() => this.filteredRecords().length);
  protected readonly soldCount = computed(() => this.soldRecords().length);
  protected readonly unsoldCount = computed(() => this.filteredUnsoldRecords().length);
  protected readonly offersCount = computed(() => this.offers().length);
  protected readonly stockCount = computed(() => this.stock().length);
  protected readonly contactsCount = computed(() => this.contacts().length);
  protected readonly hasMonthlyData = computed(
    () => this.filteredRecords().length > 0 || this.filteredUnsoldRecords().length > 0);
  protected readonly monthlyChartOptions = computed<EChartsOption>(() => {
    const soldCounts = new Map<string, number>();
    const unsoldCounts = new Map<string, number>();
    for (const r of this.soldRecords()) {
      const d = this.recordDate(r);
      if (!d) {
        continue;
      }
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      soldCounts.set(key, (soldCounts.get(key) ?? 0) + 1);
    }
    for (const r of this.filteredUnsoldRecords()) {
      const d = this.recordDate(r);
      if (!d) {
        continue;
      }
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      unsoldCounts.set(key, (unsoldCounts.get(key) ?? 0) + 1);
    }

    const labels: string[] = [];
    const soldData: number[] = [];
    const unsoldData: number[] = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(this.selectedYear(), i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      labels.push(d.toLocaleDateString('th-TH', {month: 'short'}));
      soldData.push(soldCounts.get(key) ?? 0);
      unsoldData.push(unsoldCounts.get(key) ?? 0);
    }

    return {
      tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'}},
      legend: {data: ['ขายแล้ว', 'ยังไม่ขาย'], bottom: 0, textStyle: {color: '#6c757d', fontSize: 12}},
      grid: {left: 8, right: 8, bottom: 32, top: 8, containLabel: true},
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: {lineStyle: {color: '#dee2e6'}},
        axisTick: {show: false},
        axisLabel: {color: '#6c757d', fontSize: 12},
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: {color: '#6c757d', fontSize: 12},
        splitLine: {lineStyle: {color: '#f0f0f0'}},
      },
      series: [
        {
          name: 'ขายแล้ว',
          type: 'bar',
          data: soldData,
          barMaxWidth: 36,
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [{offset: 0, color: '#4ade80'}, {offset: 1, color: '#16a34a'}],
            } as never,
            borderRadius: [4, 4, 0, 0],
          },
        },
        {
          name: 'ยังไม่ขาย',
          type: 'bar',
          data: unsoldData,
          barMaxWidth: 36,
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [{offset: 0, color: '#fbbf24'}, {offset: 1, color: '#d97706'}],
            } as never,
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  });

  ngOnInit(): void {
    this.tradeApi.listRecords().subscribe({
      next: (records) => {
        const items = records ?? [];
        this.records.set(items);
        // "Unsold" = stock still on the record; SHIPMENT legs never hold stock.
        this.unsoldRecords.set(items.filter((record) => (record.remainingQty ?? 0) > 0));
        this.loading.set(false);
      },
      error: () => {
        this.records.set([]);
        this.unsoldRecords.set([]);
        this.loading.set(false);
      },
    });

    this.tradeApi.listOffers().subscribe({
      next: (offers) => this.offers.set(offers ?? []),
      error: () => this.offers.set([]),
    });

    this.tradeApi.listStock().subscribe({
      next: (stock) => this.stock.set((stock ?? []).filter((item) => Number(item.quantity ?? 0) > 0)),
      error: () => this.stock.set([]),
    });

    this.tradeApi.listProductNames().subscribe({
      next: (productNames) => {
        this.productNames.set(productNames ?? []);
        this.loadingProductNames.set(false);
      },
      error: () => {
        this.productNames.set([]);
        this.loadingProductNames.set(false);
      },
    });

    this.chatApi.contacts().subscribe({
      next: (contacts) => this.contacts.set(
        (contacts ?? []).sort((a, b) => this.time(b.lastMessageAt) - this.time(a.lastMessageAt)),
      ),
      error: () => this.contacts.set([]),
    });
  }

  protected onYearChange(event: Event): void {
    const year = Number((event.target as HTMLSelectElement).value);
    if (Number.isInteger(year)) {
      this.selectedYear.set(year);
    }
  }

  protected onProductNameChange(event: Event): void {
    this.selectedProductName.set((event.target as HTMLSelectElement).value.trim());
  }

  protected qty(record: TradeRecordDto): string {
    return record.quantity == null ? '-' : formatQuantityWithUnit(record.quantity, record.unit);
  }

  /** How much of this record has already been bought or shipped onwards. */
  protected soldQty(record: TradeRecordDto): number {
    return record.soldQty ?? Math.max(0, (record.quantity ?? 0) - (record.remainingQty ?? 0));
  }

  protected buyerName(record: TradeRecordDto): string {
    return record.kind === 'HARVEST' ? '-' : record.businessTitle || '-';
  }

  protected sellerName(record: TradeRecordDto): string {
    return (record.kind === 'HARVEST' ? record.businessTitle : record.sellerBusinessTitle) || '-';
  }

  private matchesDashboardFilters(record: TradeRecordDto): boolean {
    const recordDate = this.recordDate(record);
    if (!recordDate || recordDate.getFullYear() !== this.selectedYear()) {
      return false;
    }
    const productName = this.selectedProductName();
    return !productName || record.productName?.trim() === productName;
  }

  private recordDate(record: TradeRecordDto): Date | null {
    return this.toDate(record.recordedAt ?? record.updatedAt ?? record.harvestDate);
  }

  private toDate(value: Date | string | undefined | null): Date | null {
    if (!value) {
      return null;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private time(value: Date | string | undefined | null): number {
    if (!value) {
      return 0;
    }
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
}
